import { useId, useState } from 'react'
import type { Ref } from 'react'
import { Card } from '../../components/ui/Card'
import { cx } from '../../components/ui/cx'
import { ChevronDownGlyph, SettingsGlyph } from '../../components/ui/lucide-glyphs'
import type { components } from '../../lib/api/schema.gen'
import { AddOnServicesCard } from './AddOnServicesCard'
import { TargetingCard } from './TargetingCard'
import { requestDetailsRecap } from './review-domain'
import type { RfqReviewLine, RfqReviewService } from './review-domain'

type RfqRegion = components['schemas']['RfqRegion']

export interface RequestDetailsSectionProps {
  // ── Targeting ──
  readonly needByDate: string
  readonly onNeedByDateChange: (value: string) => void
  readonly needByDateError?: string | undefined
  readonly needByDateInputRef?: Ref<HTMLInputElement>
  readonly region: RfqRegion
  readonly onRegionChange: (value: RfqRegion) => void
  readonly notes: string
  readonly onNotesChange: (value: string) => void
  // ── Add-on services ──
  readonly lines: readonly RfqReviewLine[]
  readonly services: readonly RfqReviewService[]
  readonly onAdd: (service: RfqReviewService) => void
  readonly onRemove: (localId: string) => void
  readonly noServicesAck: boolean
  readonly onNoServicesAckChange: (next: boolean) => void
}

/**
 * UID-242 Item 5 — collapses the RFQ compose screen's previously
 * always-expanded cards (Targeting, Add-on services) into ONE
 * collapsible section.
 *
 * UID-333 D2 (ADR-0116) — `ShippingCard` USED to be mounted here too, and
 * that was the defect: this section is default-collapsed, so the ship-to and
 * ship-method pickers were invisible on first load. The design puts both in
 * the summary rail (`dc.html:4341-4356`), and they MOVED there — they are not
 * duplicated here. See `RequestSummaryRail`. Default-collapsed (summary header row only);
 * expands on clicking the header/chevron.
 *
 * NO existing Collapse/Accordion/Disclosure primitive was found in this
 * codebase (`apps/web/src` has no shared component of that kind — the
 * closest precedent is `AssemblyTree.tsx`'s own local, hand-rolled
 * expand/collapse row, which this follows: a real `<button>` toggling
 * `aria-expanded` + `aria-controls`, with the controlled region only mounted
 * in the DOM while expanded — same shape `RfqCard.tsx`'s "Bundled" badge
 * popover already uses elsewhere on this page). This is that same established
 * local pattern, not a new one.
 *
 * UID-333 F2 (ADR-0116) — the chevron is no longer a `RightOutlined`/
 * `DownOutlined` SWAP (as this comment previously described): it is one
 * `ChevronDownGlyph` that rotates 180deg via `.rfq-details-chevron-open`,
 * matching the design's `transition:transform .2s` (`dc.html:4251`, `:9756`).
 * Two swapped glyphs share no node and so can never animate.
 *
 * UID-359 (item 3) — the completion CHIP IS GONE. It previously rendered
 * unconditionally as either a green "Complete" or an amber "Needs input",
 * derived from `rfqSubmitBlockers`. Neither ever appears in the design: the
 * reference does define a `Needs input` tag (`dc.html:4250`) but gates it on
 * `reqIncomplete`, which is `!reqComplete`, and `reqComplete` is the literal
 * constant `true` (`:9685`) — so the tag is dead code in the design and the
 * header is only ever icon + title + recap + chevron. Losing it costs nothing:
 * every blocker it hinted at is already stated, visibly and in full, in the
 * summary rail's amber callout (`RequestSummaryRail`'s `submitBlockerMessages`),
 * which is also the surface that actually gates Submit.
 *
 * UID-359 (item 3, second half) — the subtitle is now a LIVE recap of what the
 * customer has set (`requestDetailsRecap`, mirroring the design's
 * `reqSummary`/`reqRecapParts` at `:9757`/`:9721`), falling back to the generic
 * prompt only while nothing is set. It used to be that fallback string
 * hardcoded, so a fully-filled-in section still advertised itself as empty.
 */
export function RequestDetailsSection({
  needByDate,
  onNeedByDateChange,
  needByDateError,
  needByDateInputRef,
  region,
  onRegionChange,
  notes,
  onNotesChange,
  lines,
  services,
  onAdd,
  onRemove,
  noServicesAck,
  onNoServicesAckChange,
}: RequestDetailsSectionProps) {
  const [expanded, setExpanded] = useState(false)
  const headingId = useId()
  const panelId = useId()

  const recap = requestDetailsRecap({
    needByDate,
    region,
    notes,
    servicesCount: services.length,
  })

  return (
    <Card className="rfq-request-details-card">
      {/* UID-359 Gate-5 heading-order fix — an earlier fix restored the
       * deleted `<h3>`s inside `TargetingCard`/`AddOnServicesCard` (WCAG
       * 1.3.1), but this section's own disclosure title is a `<span>` inside
       * a `<button>`, not a heading node. Because `RequestDetailsSection`
       * renders FIRST in `.rfq-review-main` (UID-333 F1's DOM reorder), the
       * assembled page outline skipped straight from the page `<h1>`
       * (`RfqReviewPage.tsx`) to those `<h3>`s — a level skip. Wrapping the
       * button in a real `<h2>` is the standard APG disclosure pattern
       * (heading wraps the toggle button) and restores `h1 → h2 → h3 → h3 →
       * h2 → h2` with no skipped level, matching `#rfq-parts-heading`/
       * `#rfq-summary-heading` downstream. The `headingId` stays on the
       * `<button>` (unchanged — it's what `aria-labelledby` on the expanded
       * panel points to); the `<h2>` carries no id and adds no competing
       * accessible name, since it has no attributes of its own beyond the
       * class below. `.rfq-request-details-heading` (added to `rfq.css`)
       * neutralizes the UA default `<h2>` sizing (1.5em/bold) with
       * `font: inherit`, the same technique the button itself already uses,
       * so the button — which also declares `font: inherit` — keeps
       * inheriting the card's ordinary body font exactly as before this
       * wrapper existed. */}
      <h2 className="rfq-request-details-heading">
        <button
          type="button"
          id={headingId}
          className="rfq-request-details-header"
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={() => setExpanded((current) => !current)}
        >
          {/* UID-333 F2 (ADR-0116) — the design's 30x30 lavender gear tile
           * (`dc.html:4247`: `background:#F3EEFF; color:#8b5cf6`), immediately
           * left of the title. Purely decorative: the button's own text is its
           * accessible name, so this is `aria-hidden`. Reuses the existing
           * `SettingsGlyph` (the same Lucide "settings" glyph the design
           * inlines) rather than adding a second gear to the icon set. */}
          <span className="rfq-details-gear-chip" aria-hidden="true">
            <SettingsGlyph />
          </span>
          <span className="rfq-request-details-header-text">
            {/* UID-359 Gate-5 fidelity fix — `.heading-text` is undefined
             * app-wide (this file's own doc comments below explain why a
             * global rule would be the wrong fix), so this title was
             * inheriting the button's `font: inherit` body copy (14px/500)
             * instead of the design's 700 weight. Same id-rescue precedent as
             * `#rfq-parts-heading`/`#rfq-summary-heading`/
             * `#rfq-detail-services-heading`: the id is additive, styled by a
             * scoped `#rfq-request-details-title` rule in `rfq.css` (not
             * mine to edit). This span stays a `<span>`, not a heading — the
             * button's own text remains its accessible name, and this id is
             * for styling only. The surrounding `<h2>` above is what makes
             * this title reachable as a heading now; this span itself still
             * isn't one, and still isn't referenced by any
             * `aria-labelledby`. */}
            <span id="rfq-request-details-title" className="heading-text">
              Request details &amp; services
            </span>
            <span className="rfq-request-details-subtitle">{recap}</span>
          </span>
          {/* UID-333 F2 — ONE chevron that ROTATES, not two glyphs swapped in
           * and out. The design's `reqChevron` is
           * `transform:rotate(180deg)` applied to a single down-chevron
           * (`dc.html:4251`, `:9756`) with a `.2s` transition; swapping
           * `RightOutlined`/`DownOutlined` (as this did) can never animate,
           * because there is no shared node to transition. */}
          <span
            className={cx('rfq-details-chevron', expanded && 'rfq-details-chevron-open')}
            aria-hidden="true"
          >
            <ChevronDownGlyph />
          </span>
        </button>
      </h2>

      {expanded && (
        <section id={panelId} aria-labelledby={headingId} className="rfq-request-details-body">
          <TargetingCard
            needByDate={needByDate}
            onNeedByDateChange={onNeedByDateChange}
            needByDateError={needByDateError}
            needByDateInputRef={needByDateInputRef}
            region={region}
            onRegionChange={onRegionChange}
            notes={notes}
            onNotesChange={onNotesChange}
          />

          <AddOnServicesCard
            lines={lines}
            services={services}
            onAdd={onAdd}
            onRemove={onRemove}
            noServicesAck={noServicesAck}
            onNoServicesAckChange={onNoServicesAckChange}
          />
        </section>
      )}
    </Card>
  )
}
